import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { BaseWidget } from "../shared";
import { getWidgetState } from "orion-components/Profiles/Selectors";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { selectedContextSelector } from "orion-components/Profiles/ProfileState/selectors";
import {
	getCompositeDeviceSelector,
	selectDevicesByCompositeDeviceId
} from "../../../GlobalData/CompositeDevices/selectors";
import { Button } from "@mui/material";
import CompositeDeviceTree from "../../../DeviceManager/DeviceTree/CompositeDeviceTree";
import { controlClicked } from "../../../SharedActions/deviceProfileActions";
import { getTranslation, Translate } from "orion-components/i18n";
import IconButton from "@mui/material/IconButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EventBus from "orion-components/SharedComponents/EventBus";

const propTypes = {
	id: PropTypes.string,
	contextId: PropTypes.string,
	isForAlertProfile: PropTypes.bool,
	enabled: PropTypes.bool,
	deviceData: PropTypes.object,
	deviceStatus: PropTypes.string
};

const defaultProps = {
	deviceData: {},
	enabled: false,
	isForAlertProfile: false,
	contextId: "",
	id: "",
	deviceStatus: ""
};
const getUniqueCommandsAndDeviceIds = devices => {
	const uniqueCommands = new Map();
	const commandDeviceMap = new Map();

	devices.forEach(device => {
		const commands = device.entityData?.properties?.commands || [];

		commands.forEach(cmd => {
			if (!uniqueCommands.has(cmd.command)) {
				uniqueCommands.set(cmd.command, cmd); // Store only unique commands
				commandDeviceMap.set(cmd.command, []); // Initialize device ID array for this command
			}

			// Associate device ID with command
			commandDeviceMap.get(cmd.command).push(device.entityData.properties.id);
		});
	});

	return {
		commandsArray: Array.from(uniqueCommands.values()), // Convert Map to array
		commandDeviceMap: Object.fromEntries(commandDeviceMap) // Convert Map to object
	};
};

const CompositeDeviceAccessControlWidget = props => {
	const dispatch = useDispatch();

	const { id, enabled, selectWidget, expanded, widgetsExpandable, selected, enableActionIcon, readOnly } = props;
	const context = useSelector(state => selectedContextSelector(state));
	const selectedCompositeDevice = context?.entity;
	const childDevices = useSelector(state =>
		selectDevicesByCompositeDeviceId(state, selectedCompositeDevice?.id, true)
	);

	const commands = getUniqueCommandsAndDeviceIds(childDevices);

	const widgetEnabled = useSelector(state => getWidgetState(state)(id, "enabled") || enabled);

	const dir = useSelector(state => getDir(state));
	const compositeDeviceTreeData = useSelector(state =>
		getCompositeDeviceSelector(state, { compositeDeviceId: selectedCompositeDevice?.id })
	);

	const handleBasicDeviceCommandClick = data => {
		const selectedCommandWithDeviceMap = commands?.commandDeviceMap[data.command];
		selectedCommandWithDeviceMap.forEach(value => {
			childDevices.map(device => {
				if (device?.id === value) dispatch(controlClicked(data, device.id));
			});
		});
	};

	const handleExpand = () => {
		dispatch(selectWidget("access_control"));
	};

	const styles = {
		iconButton: {
			height: 45,
			...(dir === "rtl" && { marginRight: "10px" }),
			...(dir === "ltr" && { marginLeft: "10px" })
		}
	};

	const handleClick = event => {
		EventBus.publish("accessControl_actionIconClick", { event, node: selectedCompositeDevice });
	};

	const actionIcon = () => {
		return enableActionIcon ? (
			<div style={styles.iconButton}>
				<IconButton onClick={handleClick} style={{ color: "#FFF", width: "auto", padding: 0, height: "100%" }} data-testid="orion-components-profiles-widgets-access-control-composite-device-access-control-widget-icon-button">
					<MoreHorizIcon />
				</IconButton>
			</div>
		) : (
			<Fragment />
		);
	};

	return selected || !enabled ? (
		<Fragment />
	) : (
		<BaseWidget
			enabled={widgetEnabled}
			title={getTranslation("global.profiles.compositeDeviceProfile.accessControlWidget.title")}
			dir={dir}
			expandable={widgetsExpandable}
			handleExpand={handleExpand}
			expanded={expanded}
			showIcons={actionIcon()}
		>
			<div
				data-testid="composite-device-access-control-widget"
				style={{
					paddingTop: 22,
					display: "flex",
					maxHeight: 392,
					flexWrap: "wrap",
					marginBottom: 10,
					overflowY: "auto"
				}}
			>
				<div style={{ display: "flex", flexDirection: "column", flexWrap: "wrap" }}>
					<div style={{ display: "flex", gap: "7.8px", flexWrap: "wrap", maxWidth: 580 }}>
						{commands?.commandsArray?.length > 0 ? (
							commands?.commandsArray.map((value, index) => {
								return (
									<Button
										data-testid="composite-device-access-control-command-button"
										key={index}
										variant="contained"
										sx={{
											width: "88.66px",
											textAlign: "center",
											fontFamily: "Roboto, sans-serif",
											fontSize: "8px",
											fontWeight: "normal",
											lineHeight: "10px",
											letterSpacing: "0px",
											color: "#FFFFFF",
											opacity: 1,
											padding: "6.25px 10px !important"
										}}
										onClick={() => handleBasicDeviceCommandClick(value)}
									>
										{value.label}
									</Button>
								);
							})
						) : (
							<></>
						)}
					</div>
				</div>
				<div style={commands?.commandsArray?.length > 0 ? { marginTop: 26.69 } : {}}>
					<div
						style={{
							textAlign: "left",
							fontFamily: "Roboto",
							fontSize: "9px",
							fontWeight: "normal",
							lineHeight: "11px",
							letterSpacing: "0px",
							color: "#FFFFFF",
							opacity: 1,
							marginBottom: 10.29
						}}
					>
						<Translate value="global.profiles.compositeDeviceProfile.accessControlWidget.devices" />
					</div>
					<CompositeDeviceTree externalTreeData={compositeDeviceTreeData} readOnly={readOnly} />
				</div>
			</div>
		</BaseWidget>
	);
};

CompositeDeviceAccessControlWidget.propTypes = propTypes;
CompositeDeviceAccessControlWidget.defaultProps = defaultProps;
export default CompositeDeviceAccessControlWidget;
